import { useState, useCallback } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import EmailDebugPanel from "@/components/EmailDebugPanel";
import { parseFunctionError, type EmailDebugError } from "@/lib/emailDebug";

/**
 * Bilingual brochure-page contact form. Submits to the existing
 * `send-contact-email` edge function which routes inquiries to
 * sales@arabianalwaha.com (server-side validated + rate limited).
 */

// Mirror the server-side Zod rules so client + server agree.
const schema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(254),
  company: z.string().trim().max(200).optional().or(z.literal("")),
  phone: z
    .string()
    .trim()
    .min(1)
    .max(50)
    .regex(/^[\d\s+()-]+$/, "phone"),
  message: z.string().trim().min(1).max(2000),
});

type FormData = z.infer<typeof schema>;
type FieldErrors = Partial<Record<keyof FormData, string>>;

interface Props {
  isArabic: boolean;
}

const initialData: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  phone: "",
  message: "",
};

const BrochureContactForm = ({ isArabic }: Props) => {
  const [data, setData] = useState<FormData>(initialData);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [honeypot, setHoneypot] = useState(""); // bot trap, must stay empty
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitError, setSubmitError] = useState<EmailDebugError | null>(null);

  const t = isArabic
    ? {
        eyebrow: "تواصل معنا",
        title: "أرسل لنا استفسارك",
        subtitle: "نرد على جميع الاستفسارات خلال ساعات. سيتم إرسال رسالتك إلى فريق المبيعات مباشرة.",
        firstName: "الاسم الأول",
        lastName: "اسم العائلة",
        email: "البريد الإلكتروني",
        company: "الشركة (اختياري)",
        phone: "رقم الهاتف",
        message: "تفاصيل الاستفسار",
        messagePlaceholder: "أخبرنا عن مشروعك أو متطلباتك...",
        submit: "إرسال الاستفسار",
        sending: "جاري الإرسال...",
        successTitle: "تم إرسال رسالتك بنجاح",
        successDesc: "شكراً لتواصلك. سيقوم فريقنا بالرد عليك قريباً على sales@arabianalwaha.com.",
        sendAnother: "إرسال رسالة أخرى",
        errorTitle: "تعذر إرسال الرسالة",
        errorDesc: "يرجى المحاولة مرة أخرى أو مراسلتنا مباشرة على sales@arabianalwaha.com.",
        required: "هذا الحقل مطلوب",
        invalidEmail: "بريد إلكتروني غير صالح",
        invalidPhone: "رقم هاتف غير صالح",
      }
    : {
        eyebrow: "Get in touch",
        title: "Send us an enquiry",
        subtitle:
          "We respond to all enquiries within hours. Your message goes directly to our sales team.",
        firstName: "First name",
        lastName: "Last name",
        email: "Email",
        company: "Company (optional)",
        phone: "Phone",
        message: "How can we help?",
        messagePlaceholder: "Tell us about your project or requirements...",
        submit: "Send enquiry",
        sending: "Sending...",
        successTitle: "Your message was sent",
        successDesc:
          "Thank you for reaching out. Our team will reply shortly from sales@arabianalwaha.com.",
        sendAnother: "Send another message",
        errorTitle: "We couldn't send your message",
        errorDesc:
          "Please try again, or email us directly at sales@arabianalwaha.com.",
        required: "This field is required",
        invalidEmail: "Please enter a valid email",
        invalidPhone: "Please enter a valid phone number",
      };

  const onChange =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setData((d) => ({ ...d, [field]: e.target.value }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  const validate = useCallback((): boolean => {
    const result = schema.safeParse(data);
    if (result.success) {
      setErrors({});
      return true;
    }
    const next: FieldErrors = {};
    for (const issue of result.error.issues) {
      const key = issue.path[0] as keyof FormData;
      if (next[key]) continue;
      if (key === "email") next[key] = t.invalidEmail;
      else if (key === "phone") next[key] = t.invalidPhone;
      else next[key] = t.required;
    }
    setErrors(next);
    return false;
  }, [data, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Honeypot — silently succeed for bots
      if (honeypot.trim() !== "") {
        setStatus("success");
        return;
      }
      if (!validate()) return;

      setSubmitting(true);
      setStatus("idle");
      setSubmitError(null);
      try {
        if (!isSupabaseConfigured() || !supabase) {
          throw new Error("Email service not configured");
        }
        const { error } = await supabase.functions.invoke("send-contact-email", {
          body: { ...data, honeypot: "" },
        });
        if (error) throw error;
        setStatus("success");
        import("@/config/analytics").then(m => m.trackConversion("brochure_form_submit"));
        setSubmitError(null);
        setData(initialData);
        toast.success(t.successTitle);
      } catch (err) {
        console.error("Brochure contact submit failed:", err);
        const parsedError = await parseFunctionError(err, t.errorTitle, t.errorDesc);
        setSubmitError(parsedError);
        setStatus("error");
        toast.error(t.errorTitle);
      } finally {
        setSubmitting(false);
      }
    },
    [data, honeypot, validate, t]
  );

  if (status === "success") {
    return (
      <div
        className="rounded-2xl border border-primary/20 bg-primary/5 p-10 text-center"
        role="status"
        aria-live="polite"
      >
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 className="h-7 w-7 text-primary" aria-hidden="true" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2">{t.successTitle}</h3>
        <p className="text-muted-foreground max-w-md mx-auto mb-6">{t.successDesc}</p>
        <Button
          type="button"
          variant="outline"
          onClick={() => setStatus("idle")}
        >
          {t.sendAnother}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-border/60 bg-card p-6 md:p-10 shadow-sm"
      dir={isArabic ? "rtl" : "ltr"}
      noValidate
    >
      {/* Honeypot — hidden from users, visible to bots */}
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label>
          Leave this field empty
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field
          id="bcf-firstName"
          label={t.firstName}
          required
          error={errors.firstName}
        >
          <Input
            id="bcf-firstName"
            value={data.firstName}
            onChange={onChange("firstName")}
            maxLength={100}
            autoComplete="given-name"
            disabled={submitting}
            aria-invalid={!!errors.firstName}
          />
        </Field>

        <Field id="bcf-lastName" label={t.lastName} required error={errors.lastName}>
          <Input
            id="bcf-lastName"
            value={data.lastName}
            onChange={onChange("lastName")}
            maxLength={100}
            autoComplete="family-name"
            disabled={submitting}
            aria-invalid={!!errors.lastName}
          />
        </Field>

        <Field id="bcf-email" label={t.email} required error={errors.email}>
          <Input
            id="bcf-email"
            type="email"
            value={data.email}
            onChange={onChange("email")}
            maxLength={254}
            autoComplete="email"
            disabled={submitting}
            aria-invalid={!!errors.email}
          />
        </Field>

        <Field id="bcf-phone" label={t.phone} required error={errors.phone}>
          <Input
            id="bcf-phone"
            type="tel"
            value={data.phone}
            onChange={onChange("phone")}
            maxLength={50}
            autoComplete="tel"
            disabled={submitting}
            aria-invalid={!!errors.phone}
          />
        </Field>

        <div className="md:col-span-2">
          <Field id="bcf-company" label={t.company} error={errors.company}>
            <Input
              id="bcf-company"
              value={data.company ?? ""}
              onChange={onChange("company")}
              maxLength={200}
              autoComplete="organization"
              disabled={submitting}
              aria-invalid={!!errors.company}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field id="bcf-message" label={t.message} required error={errors.message}>
            <Textarea
              id="bcf-message"
              value={data.message}
              onChange={onChange("message")}
              maxLength={2000}
              rows={5}
              placeholder={t.messagePlaceholder}
              disabled={submitting}
              aria-invalid={!!errors.message}
            />
          </Field>
        </div>
      </div>

      {status === "error" && (
        <div
          className="mt-6 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
          role="alert"
        >
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="font-semibold">{submitError?.title || t.errorTitle}</p>
            {(submitError?.details?.length ? submitError.details : [t.errorDesc]).map((detail, index) => (
              <p key={index} className="text-destructive/80">{detail}</p>
            ))}
            <EmailDebugPanel debug={submitError?.debug} />
          </div>
        </div>
      )}

      <div className="mt-7 flex justify-end">
        <Button
          type="submit"
          size="lg"
          disabled={submitting}
          className="bg-primary hover:bg-primary/90 min-w-[180px]"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
              {t.sending}
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" aria-hidden="true" />
              {t.submit}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

/* --------------------------- helper --------------------------- */

interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

const Field = ({ id, label, required, error, children }: FieldProps) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-medium">
      {label}
      {required && <span className="text-destructive ms-0.5">*</span>}
    </Label>
    {children}
    {error && (
      <p className="text-xs text-destructive flex items-center gap-1" role="alert">
        <AlertCircle className="h-3 w-3" aria-hidden="true" />
        {error}
      </p>
    )}
  </div>
);

export default BrochureContactForm;